import type { DefinitionSummary, WorkflowDefinition } from "@/stores/workflowsStore";
import { formatConnectorName } from "@/utils/string";
import { getRequiredConnectorType } from "@/utils/workflowTransform";

/** Icon to render on a workflow/template card. */
export type CardIcon =
  | { kind: "connector"; brandName: string }
  | { kind: "chain"; chainId: number }
  | { kind: "node"; type: string; isTrigger: boolean };

/** Minimal action/trigger definition shape needed for icon resolution. */
export interface CardIconDef {
  type: string;
  name?: string;
  logoUrl?: string;
  connector?: { type: string };
}

/** Input shape accepted by card-icon extraction — both full Workflow and Template fit. */
export interface CardIconSource {
  definition?: WorkflowDefinition;
  definitionSummary?: DefinitionSummary;
  connectors?: Array<{ type: string }>;
  chainIds?: number[];
}

export function isExcludedWorkflowCardBrand(brand: string): boolean {
  return brand.toLowerCase() === "turnkey";
}

function getTypeNamespace(type: string): string | null {
  const idx = type.indexOf("-");
  if (idx <= 0) return null;
  return type.slice(0, idx).toLowerCase();
}

function getConnectorBrand(type: string): string {
  return (type.includes(":") ? type.split(":")[1] : type).toLowerCase();
}

export function resolveNodeDefinition(
  type: string,
  isTrigger: boolean,
  triggers: CardIconDef[],
  actions: CardIconDef[],
): CardIconDef | undefined {
  const primaryDefs = isTrigger ? triggers : actions;
  const fallbackDefs = isTrigger ? actions : triggers;

  const exact = primaryDefs.find(def => def.type === type);
  if (exact) return exact;

  const namespace = getTypeNamespace(type);
  if (!namespace) return primaryDefs.find(def => def.type === type) || fallbackDefs.find(def => def.type === type);

  const byNamespace = [...primaryDefs, ...fallbackDefs].find(def => {
    const defNamespace = getTypeNamespace(def.type);
    return defNamespace === namespace && (!!def.logoUrl || !!def.connector?.type);
  });

  return byNamespace || primaryDefs.find(def => def.type === type) || fallbackDefs.find(def => def.type === type);
}

/**
 * Extract card icons from a workflow/template source.
 * Prefer ordered node icons so the card matches the workflow detail modal,
 * then append connector/chain context from the slim list fields.
 */
export function extractCardIcons(source: CardIconSource): CardIcon[] {
  const connectorIcons: CardIcon[] = [];
  const chainIcons: CardIcon[] = [];
  const nodeIcons: CardIcon[] = [];
  const seenConnectors = new Set<string>();
  const seenChainIds = new Set<number>();

  if (source.connectors) {
    for (const c of source.connectors) {
      const brand = c.type.includes(":") ? c.type.split(":")[1] : c.type;
      const brandLower = brand.toLowerCase();
      if (brandLower !== "wallet" && !isExcludedWorkflowCardBrand(brandLower) && !seenConnectors.has(brandLower)) {
        seenConnectors.add(brandLower);
        connectorIcons.push({ kind: "connector", brandName: brand });
      }
    }
  }

  if (source.chainIds) {
    for (const chainId of source.chainIds) {
      if (seenChainIds.has(chainId)) continue;
      seenChainIds.add(chainId);
      chainIcons.push({ kind: "chain", chainId });
    }
  }

  const defNodes = source.definition?.nodes || source.definitionSummary?.nodes || {};
  const childIds = new Set<string>();
  Object.values(defNodes).forEach(node => {
    node.children?.forEach(childId => childIds.add(childId));
    node.loopBody?.forEach(childId => childIds.add(childId));
  });

  const queue: string[] = [];
  for (const nodeId of Object.keys(defNodes)) {
    if (!childIds.has(nodeId)) {
      queue.push(nodeId);
      break;
    }
  }
  if (queue.length === 0) queue.push(...Object.keys(defNodes));

  const visited = new Set<string>();
  const seenNodeTypes = new Set<string>();

  while (queue.length > 0) {
    const nodeId = queue.shift()!;
    if (visited.has(nodeId)) continue;
    visited.add(nodeId);
    const node = defNodes[nodeId];
    if (!node) continue;

    if (!seenNodeTypes.has(node.type)) {
      seenNodeTypes.add(node.type);
      const isRoot = !childIds.has(nodeId);
      nodeIcons.push({ kind: "node", type: node.type, isTrigger: isRoot });
    }

    node.children?.forEach(childId => queue.push(childId));
    node.loopBody?.forEach(childId => queue.push(childId));
  }

  return [...nodeIcons, ...connectorIcons, ...chainIcons];
}

/**
 * Compute a visual-identity key so two icons that would render the same
 * (same brand/logo/namespace) collapse to one tile on the card.
 */
export function getCardIconVisualKey(icon: CardIcon, triggers: CardIconDef[], actions: CardIconDef[]): string | null {
  if (icon.kind === "connector") {
    const brand = icon.brandName.toLowerCase();
    return isExcludedWorkflowCardBrand(brand) ? null : `provider:${brand}`;
  }

  if (icon.kind === "chain") {
    return `chain:${icon.chainId}`;
  }

  const def = resolveNodeDefinition(icon.type, icon.isTrigger, triggers, actions);
  const reqConnType = getRequiredConnectorType(icon.type, def);
  const namespace = getTypeNamespace(icon.type);

  if (reqConnType) {
    const brand = getConnectorBrand(reqConnType);
    if (isExcludedWorkflowCardBrand(brand)) {
      return null;
    }
    if (brand !== "wallet") {
      return `provider:${brand}`;
    }
  }

  if (namespace && def?.logoUrl) {
    if (isExcludedWorkflowCardBrand(namespace)) {
      return null;
    }
    return `provider:${namespace}`;
  }

  if (def?.logoUrl) {
    return `logo:${def.logoUrl}`;
  }

  if (namespace) {
    return `namespace:${namespace}`;
  }

  return `node:${icon.type}:${icon.isTrigger ? "trigger" : "action"}`;
}

/**
 * True when an icon will render a concrete brand/chain/connector logo (Slack,
 * Arbitrum, ETH, etc.) rather than falling through to an AI-generated
 * `b3os-app-flat` tile. Used to prefer recognizable logos when card real
 * estate is limited.
 */
export function hasBrandVisual(icon: CardIcon, triggers: CardIconDef[], actions: CardIconDef[]): boolean {
  if (icon.kind === "connector") return true;
  if (icon.kind === "chain") return true;
  const def = resolveNodeDefinition(icon.type, icon.isTrigger, triggers, actions);
  if (def?.logoUrl) return true;
  const reqConn = getRequiredConnectorType(icon.type, def);
  if (reqConn && getConnectorBrand(reqConn) !== "wallet") return true;
  return false;
}

/** Brand-only / generic-only buckets from a single pass through `icons`. */
export function partitionCardIcons(
  icons: CardIcon[],
  triggers: CardIconDef[],
  actions: CardIconDef[],
): { brand: CardIcon[]; generic: CardIcon[] } {
  const brand: CardIcon[] = [];
  const generic: CardIcon[] = [];
  for (const icon of icons) {
    (hasBrandVisual(icon, triggers, actions) ? brand : generic).push(icon);
  }
  return { brand, generic };
}

/**
 * Stable reordering that surfaces logo-backed icons (connectors / chains /
 * nodes with `def.logoUrl` or a required connector) before AI-icon-only
 * nodes. Callers that cap display count should prefer this over raw order so
 * recognizable brands aren't pushed off-card by generic logic nodes.
 */
export function prioritizeCardIcons(icons: CardIcon[], triggers: CardIconDef[], actions: CardIconDef[]): CardIcon[] {
  const { brand, generic } = partitionCardIcons(icons, triggers, actions);
  return [...brand, ...generic];
}

/** Dedup icons by their visual key, dropping nulls. */
export function dedupeCardIcons(icons: CardIcon[], triggers: CardIconDef[], actions: CardIconDef[]): CardIcon[] {
  const seen = new Set<string>();
  return icons.filter(icon => {
    const key = getCardIconVisualKey(icon, triggers, actions);
    if (!key) return false;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

/** Minimal chain info used for card logos (satisfied by the chain registry). */
export interface CardIconChainInfo {
  name?: string;
  iconUrl?: string | null;
}

/** Minimal connector-type registry entry used to gate brand-logo lookups. */
export interface CardIconConnectorType {
  type: string;
  logoUrl?: string;
}

function toIntVal(v: unknown): number {
  if (typeof v === "number" && Number.isFinite(v)) return v;
  if (typeof v === "string") {
    const n = Number(v);
    if (Number.isFinite(n)) return n;
  }
  return 0;
}

/**
 * Client-side mirror of the backend's `computeChainIds` — walks a full
 * definition and pulls chain ids out of each node's `payload.chainId` /
 * `payload.chainIds`. Templates load the full definition, so they can run
 * this locally instead of relying on the workflow list's slim `chainIds`.
 */
export function extractChainIdsFromDefinition(definition: WorkflowDefinition | null | undefined): number[] {
  if (!definition?.nodes) return [];
  const seen = new Set<number>();
  for (const node of Object.values(definition.nodes)) {
    const payload = (node as { payload?: Record<string, unknown> }).payload;
    if (!payload) continue;
    const single = payload.chainId;
    if (single !== undefined) {
      const id = toIntVal(single);
      if (id) seen.add(id);
    }
    const multi = payload.chainIds;
    if (Array.isArray(multi)) {
      for (const v of multi) {
        const id = toIntVal(v);
        if (id) seen.add(id);
      }
    }
  }
  return [...seen].sort((a, b) => a - b);
}

/** Client-side mirror of the backend's `computeConnectors`. */
export function extractConnectorsFromDefinition(
  definition: WorkflowDefinition | null | undefined,
): Array<{ type: string }> {
  if (!definition?.nodes) return [];
  const seen = new Set<string>();
  const result: Array<{ type: string }> = [];
  for (const node of Object.values(definition.nodes)) {
    const type = node.connector?.type;
    if (!type || seen.has(type)) continue;
    seen.add(type);
    result.push({ type });
  }
  return result;
}

/** Pill-ready metadata for the template detail "Integrate with" list. */
export interface IntegrationPill {
  key: string;
  kind: "brand" | "chain" | "wallet";
  label: string;
  logoUrl: string;
  /** Connector type for <ConnectorPill> rendering (falls back to brand label). */
  connectorType?: string;
}

function getConnectorLogoUrlFromBrand(brand: string): string {
  return `https://ai-icon.b3.fun/logo/${encodeURIComponent(brand.replace(/\s+/g, "_"))}`;
}

/**
 * Derive "Integrate with" pills from a full workflow/template definition.
 * Three sources, ordered so brand logos (Morpho, Telegram) surface ahead of
 * chains (Base) and the catch-all Wallet pill.
 *
 * - **brand** pills come from (a) node `connector.type` (non-wallet) or
 *   (b) a node with a type namespace + `def.logoUrl` (e.g. `morpho-*`).
 * - **chain** pills come from `payload.chainId` / `payload.chainIds` plus the
 *   chain registry (for name + iconUrl).
 * - **wallet** pill appears once if any node uses a wallet connector and no
 *   stronger brand applied for that node.
 */
export function getTemplateIntegrationPills(
  definition: WorkflowDefinition | null | undefined,
  triggers: CardIconDef[],
  actions: CardIconDef[],
  chainInfoMap: Map<number, CardIconChainInfo> | undefined,
): IntegrationPill[] {
  if (!definition?.nodes) return [];

  const brandPills = new Map<string, IntegrationPill>();
  let hasWalletOnlyNode = false;

  for (const node of Object.values(definition.nodes)) {
    const def = resolveNodeDefinition(node.type, false, triggers, actions);
    const reqConnType = node.connector?.type || getRequiredConnectorType(node.type, def);
    const namespace = getTypeNamespace(node.type);

    let matchedBrand = false;

    // Non-wallet connector wins — Slack, Telegram, Gmail, etc.
    if (reqConnType) {
      const brand = getConnectorBrand(reqConnType);
      if (!isExcludedWorkflowCardBrand(brand) && brand !== "wallet") {
        const key = `brand:${brand}`;
        if (!brandPills.has(key)) {
          brandPills.set(key, {
            key,
            kind: "brand",
            label: formatConnectorName(brand),
            logoUrl: getConnectorLogoUrlFromBrand(brand),
            connectorType: reqConnType,
          });
        }
        matchedBrand = true;
      }
    }

    // Brand-backed action like `morpho-vault-deposit` — prefer the namespace
    // brand over the "Wallet" connector it also requires.
    if (!matchedBrand && namespace && def?.logoUrl && !isExcludedWorkflowCardBrand(namespace)) {
      const key = `brand:${namespace}`;
      if (!brandPills.has(key)) {
        brandPills.set(key, {
          key,
          kind: "brand",
          label: formatConnectorName(namespace),
          logoUrl: def.logoUrl,
          connectorType: namespace,
        });
      }
      matchedBrand = true;
    }

    // Track wallet-only nodes so we render a single wallet pill at the end.
    if (!matchedBrand && reqConnType && getConnectorBrand(reqConnType) === "wallet") {
      hasWalletOnlyNode = true;
    }
  }

  const chainIds = extractChainIdsFromDefinition(definition);
  const chainPills: IntegrationPill[] = [];
  for (const chainId of chainIds) {
    const chain = chainInfoMap?.get(chainId);
    if (!chain) continue;
    const logoUrl = chain.iconUrl || (chain.name ? getConnectorLogoUrlFromBrand(chain.name) : "");
    if (!logoUrl) continue;
    chainPills.push({
      key: `chain:${chainId}`,
      kind: "chain",
      label: chain.name || `Chain ${chainId}`,
      logoUrl,
    });
  }

  const pills: IntegrationPill[] = [...brandPills.values(), ...chainPills];

  if (hasWalletOnlyNode) {
    pills.push({
      key: "wallet",
      kind: "wallet",
      label: "Wallet",
      logoUrl: getConnectorLogoUrlFromBrand("wallet"),
      connectorType: "wallet",
    });
  }

  return pills;
}
